<template>
    <div class="cf-container">
        <md-table v-if="hasDeliveryWindows">
            <md-table-row slot="header">
                <md-table-head class="time-range-columns">From</md-table-head>
                <md-table-head class="time-range-columns">To</md-table-head>
                <md-table-head class="max-stops-column">Max stops</md-table-head>
                <md-table-head v-if="hasCapacitySettings('volume')" class="capacity-settings-columns">
                    Max volume
                </md-table-head>
                <md-table-head v-if="hasCapacitySettings('weight')" class="capacity-settings-columns">
                    Max weight
                </md-table-head>
                <md-table-head v-if="hasCapacitySettings('quantity')" class="capacity-settings-columns">
                    Max quantity
                </md-table-head>
                <md-table-head class="occurrence-column">Occurrence</md-table-head>
                <md-table-head class="occurrence-date-column">Occ. Start Date</md-table-head>
                <md-table-head class="cut-off-label-column">Cut off</md-table-head>
                <md-table-head>Actions</md-table-head>
            </md-table-row>
            <md-table-row v-for="(deliveryWindow) in filteredDeliveryWindows" :key="deliveryWindow.deliveryWindowId">
                <md-table-cell class="time-range-columns">
                    <form-group>
                        <md-icon>access_time</md-icon>
                        <time-picker
                            :time="deliveryWindow.startTime"
                            :all-time-options="false"
                            :is-required="true"
                            data-testid="deliveryWindow-startTime"
                            @selectedTime="
                                (selectedTime, isTimeValid) => {
                                    handleSetStartTime(selectedTime, isTimeValid, deliveryWindow);
                                }
                            "
                        />
                    </form-group>
                </md-table-cell>
                <md-table-cell class="time-range-columns">
                    <form-group>
                        <md-icon>access_time</md-icon>
                        <time-picker
                            :time="deliveryWindow.endTime"
                            :all-time-options="false"
                            :is-required="true"
                            data-testid="deliveryWindow-startTime"
                            @selectedTime="
                                (selectedTime, isTimeValid) => {
                                    handleSetEndTime(selectedTime, isTimeValid, deliveryWindow);
                                }
                            "
                        />
                    </form-group>
                </md-table-cell>
                <md-table-cell class="max-stops-column">
                    <form-group>
                        <md-input
                            v-model.number="deliveryWindow.maxStops"
                            type="number"
                            data-testid="deliveryWindow-maxStops"
                        />
                    </form-group>
                </md-table-cell>
                <md-table-cell v-if="hasCapacitySettings('volume')" class="capacity-settings-columns">
                    <form-group>
                        <md-input
                            v-model.number="deliveryWindow.maxVolume"
                            type="number"
                            data-testid="deliveryWindow-maxVolume"
                        />
                    </form-group>
                </md-table-cell>
                <md-table-cell v-if="hasCapacitySettings('weight')" class="capacity-settings-columns">
                    <form-group>
                        <md-input
                            v-model.number="deliveryWindow.maxWeight"
                            type="number"
                            data-testid="deliveryWindow-maxWeight"
                        />
                    </form-group>
                </md-table-cell>
                <md-table-cell v-if="hasCapacitySettings('quantity')" class="capacity-settings-columns">
                    <form-group>
                        <md-input
                            v-model.number="deliveryWindow.maxQuantity"
                            type="number"
                            data-testid="deliveryWindow-maxQuantity"
                        />
                    </form-group>
                </md-table-cell>
                <md-table-cell class="occurrence-column">
                    <form-group class="delivery-window-dropdown">
                        <md-select
                            v-model="deliveryWindow.occurrence"
                            data-testid="deliveryWindow-occurrence"
                            @md-selected="
                                (id) => {
                                    handleSetOcurrence(id, deliveryWindow);
                                }
                            "
                        >
                            <md-option
                                v-for="option in deliveryWindowOccurrenceOptions"
                                :key="option.id"
                                :value="option.id"
                            >
                                {{ option.name }}
                            </md-option>
                        </md-select>
                    </form-group>
                </md-table-cell>
                <md-table-cell class="occurrence-date-column">
                    <md-datepicker
                        v-if="deliveryWindow.occurrence === 1"
                        v-model="deliveryWindow.occurrenceStartDate"
                        md-immediately
                        data-testid="deliveryWindow-occurrenceStartDate"
                        :md-debounce="100"
                        :md-disabled-dates="disableAllExcept"
                        :md-model-type="String"
                    ></md-datepicker>
                </md-table-cell>
                <md-table-cell
                    :class="
                        `cut-off-label-column ${
                            $_isValidServiceAreaCutOffCombination(deliveryWindow, dayOfTheWeek)
                                ? ''
                                : 'invalid-cutt-off-options'
                        }`
                    "
                >
                    <md-icon>access_time</md-icon>
                    {{ $_generateServiceAreaCutOffLabel(deliveryWindow, dayOfTheWeek) }}
                    <div class="edit-cutoff-button" @click="() => handleEditCuttOff(deliveryWindow, dayOfTheWeek)">
                        <md-icon>arrow_drop_down</md-icon>
                    </div>
                </md-table-cell>
                <md-table-cell>
                    <md-button
                        title="Delete delivery window"
                        class="md-danger md-just-icon md-round"
                        @click="deleteDeliveryWindow(deliveryWindow)"
                    >
                        <md-icon>delete</md-icon>
                    </md-button>
                </md-table-cell>
            </md-table-row>
        </md-table>
        <div class="text-center" v-if="!hasDeliveryWindows">
            <p class="no-result-message">List is empty.</p>
        </div>
        <div class="add-delivery-window-container">
            <md-button class="md-button md-primary add-delivery-window-button" @click="handleAddDeliveryWindow">
                <md-icon>add</md-icon>
                Add Delivery Window
            </md-button>
        </div>
    </div>
</template>

<script>
import { TimePicker } from '@/components';
import { mapGetters } from 'vuex';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { rowStatus } from '@/helpers';
import ServiceAreasCutOffModal from '../ServiceAreasCutOffModal';

export default {
    name: 'ServiceAreasDeliveryWindow',
    components: {
        TimePicker
    },
    mixins: [GeneralMixin],
    props: {
        serviceAreaId: {
            type: String,
            default: null
        },
        dayOfTheWeek: {
            type: Number,
            default: null
        },
        deliveryWindowData: {
            type: Object,
            default: () => ({ deliveryWindows: [] })
        }
    },
    data() {
        return {
            deliveryWindowOccurrenceOptions: [
                {
                    id: 0,
                    name: 'Weekly'
                },
                {
                    id: 1,
                    name: 'Fortnightly'
                }
            ]
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        }),
        filteredDeliveryWindows() {
            return (this.deliveryWindowData?.deliveryWindows || []).filter((x) => x.rowStatus !== rowStatus.DELETE);
        },
        hasDeliveryWindows() {
            return this.deliveryWindowData?.deliveryWindows?.length > 0;
        }
    },
    methods: {
        disableAllExcept(date) {
            if (!date) {
                return true;
            }
            const day = new Date(date).getDay();
            return day !== this.dayOfTheWeek;
        },
        async deleteDeliveryWindow(deliveryWindow) {
            const index = this.deliveryWindowData.deliveryWindows.indexOf(deliveryWindow);

            if (deliveryWindow.rowStatus === rowStatus.ADD && index !== -1) {
                this.deliveryWindowData.deliveryWindows.splice(index, 1);
                return;
            }

            if (index !== -1) {
                deliveryWindow.rowStatus = rowStatus.DELETE;
            }
        },
        hasCapacitySettings(...capacityTypes) {
            const { vehicleCapacityUnitsConfiguration } = this.user;
            return (
                Array.isArray(vehicleCapacityUnitsConfiguration) &&
                vehicleCapacityUnitsConfiguration.some((x) => capacityTypes.some((y) => x.type === y))
            );
        },
        handleSetStartTime(selectedTime, isTimeValid, deliveryWindow) {
            if (isTimeValid) {
                deliveryWindow.startTime = selectedTime?.appointmentTime;
                if (![rowStatus.DELETE, rowStatus.ADD].includes(deliveryWindow.rowStatus)) {
                    deliveryWindow.rowStatus = rowStatus.EDIT;
                }
            }
        },
        handleSetEndTime(selectedTime, isTimeValid, deliveryWindow) {
            if (isTimeValid) {
                deliveryWindow.endTime = selectedTime.appointmentTime;
                if (![rowStatus.DELETE, rowStatus.ADD].includes(deliveryWindow.rowStatus)) {
                    deliveryWindow.rowStatus = rowStatus.EDIT;
                }
            }
        },
        handleSetOcurrence(value, deliveryWindow) {
            deliveryWindow.occurrence = value;
        },
        handleAddDeliveryWindow() {
            const newDeliveryWindow = {
                deliveryWindowId: null,
                startTime: null,
                endTime: null,
                maxStops: null,
                maxVolume: null,
                maxWeight: null,
                maxQuantity: null,
                occurrence: 0,
                occurrenceStartDate: null,
                cutOffType: null,
                cutOffTimeBeforeSlotValue: null,
                cutOffTimeBeforeSlotType: null,
                cutOffByTimeValue: null,
                cutOffByTimeType: null,
                cutOffByTimeNDaysPriorValue: null,
                rowStatus: rowStatus.ADD
            };

            this.deliveryWindowData.deliveryWindows.push(newDeliveryWindow);
        },
        handleEditCuttOff(deliveryWindow, dayOfTheWeek) {
            this.$modal
                .show(ServiceAreasCutOffModal, {
                    deliveryWindow,
                    currentDayOfTheWeek: dayOfTheWeek
                })
                .then((response) => {
                    Object.assign(deliveryWindow, response);
                    this.$modal.hide();
                });
        }
    }
};
</script>
<style lang="scss" scoped>
.delivery-window-dropdown {
    width: 115px;
    margin-top: 0;
    border: 1px solid #ddd;
    padding: 0;
    padding-left: 10px;
}

.delivery-window-dropdown:after,
.delivery-window-dropdown:before {
    height: 0;
}

.add-delivery-window-container {
    border-top: 1px solid #dddddd;
}

.add-delivery-window-button,
.add-delivery-window-button:hover {
    margin-left: 0;
    background-color: #5f6771 !important;
    margin-top: 10px;
}

.edit-cutoff-button {
    display: inline-block;
    cursor: pointer;
}

::v-deep .vs--searchable .vs__dropdown-toggle {
    border-bottom: 0 !important;
}

.invalid-cutt-off-options {
    color: #ff1744;
}

.max-stops-column,
.capacity-settings-columns {
    width: 115px;
    min-width: 115px;
    max-width: 115px;
    input {
        width: 95px;
    }
}

.occurrence-column {
    width: 130px;
    min-width: 130px;
    max-width: 130px;
}
.time-range-columns,
.occurrence-date-column {
    width: 175px;
    min-width: 175px;
    max-width: 175px;
}

.cut-off-label-column {
    width: 300px;
    min-width: 300px;
    max-width: 300px;
}
</style>
